import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpErrorDto, UserDto } from '@pragma/dto';
import { LoaderService, NotificationsService, PermissionService, UserPermission, UserService } from '@pragma/services';
import { cloneDeep } from 'lodash';
import { MenuItem } from 'primeng/api';
import { PanelMenuModule } from 'primeng/panelmenu';
import { SidebarModule } from 'primeng/sidebar';
import { Subscription } from 'rxjs';
import { FooterComponent } from '../footer/footer.component';
import { InputSwitchModule } from 'primeng/inputswitch';
import { FormsModule } from '@angular/forms';
import { ThemeColorSwitcherService } from '../../services/theme/theme-color-switcher.service';

@Component({
    standalone: true,
    selector: 'ngx-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        RouterModule,
        SidebarModule,
        PanelMenuModule,
        InputSwitchModule,
        FormsModule,
        TranslateModule
    ]
})
export class SidebarComponent implements OnInit, OnDestroy {
    private langChangeSubscription: Subscription = new Subscription();

    items!: MenuItem[];
    user: UserDto | undefined;
    hide = true;
    itemsWithOnlyIcons: MenuItem[] = [];
    isDarkMode: boolean = false;

    constructor(
        private _translate: TranslateService,
        private _cd: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _loaderService: LoaderService,
        private _notificationService: NotificationsService,
        private _themeColorSwitcherService: ThemeColorSwitcherService,
        private permissionService: PermissionService,
        public el: ElementRef
    ) {
        //this.items = cloneDeep(this._loaderService.sideBarItems);
    }

    ngOnInit(): void {
        this.subscribeToUserChanges();
        this.subscribeToLanguageChanges();
        this.initializeDarkMode();

        this._themeColorSwitcherService.darkModeState.subscribe((isDark) => {
            this.isDarkMode = isDark;
            this._cd.detectChanges();
        });
    }

    ngOnDestroy() {
        if (this.langChangeSubscription) {
            this.langChangeSubscription.unsubscribe();
        }
    }

    toggleDarkMode(isChecked: boolean) {
        this.isDarkMode = isChecked;
        this._themeColorSwitcherService.toggleTheme(this.isDarkMode);
        this._cd.detectChanges();
    }

    initializeDarkMode() {
        const darkModeSetting = localStorage.getItem('darkMode');
        if (darkModeSetting) {
            this.isDarkMode = darkModeSetting === 'true';
            this._themeColorSwitcherService.toggleTheme(this.isDarkMode);
        }
    }

    public setItemsWithOnlyIcons() {
        this.itemsWithOnlyIcons = this.items.map((item) => {
            const finalItem = {
                icon: item.icon,
                visible: item.visible,
                routerLink: item.routerLink,
                title: item.title,
                items: item.items?.map((subItem) => {
                    return {
                        icon: subItem.icon,
                        visible: subItem.visible,
                        routerLink: subItem.routerLink,
                        title: subItem.title
                    };
                })
            };
            return finalItem;
        });
    }

    navigateHome() {
        this._router.navigate(['admin/users']);
        this._userService.getUser().subscribe((user) => {
            this.permissionService.setRoles(user?.userRoles || []);
            if (user?.userRoles.length === 1 && (user.userRoles[0].id === 6 || user.userRoles[0].id === 7)) {
                this._router.navigate(['/pages', 'support']);
            } else {
                this._router.navigate(['admin/users']);
            }
            this._cd.detectChanges();
        });
    }

    updateSidebarItems(): void {
        this._userService.getUser().subscribe((user) => {
            this.permissionService.setRoles(user?.userRoles || []);
            this.items = this._loaderService
                .getSidebarItemsByRole(user?.userRoles)
                .map((item) => this.translateAndCheckVisibility(item));
            this._cd.detectChanges();
        });

        //this.items = this._loaderService.sideBarItems.map((item) => this.translateAndCheckVisibility(item));
        this._cd.detectChanges();
    }

    private subscribeToUserChanges(): void {
        this._userService.user.subscribe({
            next: (user) => {
                this.user = user;
                this.updateSidebarItems();
            },
            error: (err) => {
                this.handleError(err);
            }
        });
    }

    private subscribeToLanguageChanges(): void {
        this.langChangeSubscription = this._translate.onLangChange.subscribe({
            next: () => {
                this.updateSidebarItems();
            },
            error: (err: HttpErrorDto) => {
                this.handleError(err);
            }
        });
    }

    private translateAndCheckVisibility(item: MenuItem): MenuItem {
        const newItem = { ...item };
        newItem.label = this._translate.instant(newItem.label || 'default_label_key');

        if (newItem.items) {
            newItem.items = newItem.items.map((subItem) => this.translateAndCheckVisibility(subItem));
            newItem.visible = newItem.items.some((subItem) => subItem.visible);
        } else {
            newItem.visible = this._userService.hasPermission(newItem.title as UserPermission);
        }

        return newItem;
    }

    private handleError(error: HttpErrorDto): void {
        const customMessage = this._translate.instant('errors.sidebar.unableToLoad');
        this._notificationService.displayError(customMessage, error.message || 'Unknown error');

        console.error('SidebarComponent error:', error);
    }
}
